import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Card, Table, Button, Spinner, Alert, Pagination, Form, Row, Col, Badge, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FiRefreshCw, FiEye, FiCheckCircle, FiArrowLeft, FiTrash2, FiDownload, FiCloud } from 'react-icons/fi';
import { fetchTripsCheckClaimsRequest, deleteTripCheckClaimRequest } from '../../store/actions/tripsActions';
import { getAllPartners } from '../../services/api';
import { handleApiError } from '../../utils/errorHandler';
import PartnerCompanyAutocomplete from '../../components/Common/PartnerCompanyAutocomplete';
import usePersistedSearchState from '../../hooks/usePersistedSearchState';
import 'bootstrap/dist/css/bootstrap.css';
import './AdminTripsClaims.css';

const EMPTY_CHECK_CLAIMS_FILTERS = {
    reservation_number: '',
    policy_code: '',
    client_email: '',
    activity_end_date_from: '',
    activity_end_date_to: '',
    claim_code: '',
    date_of_claim: '',
    trip_day: '',
    company_name: '',
    reference_name: '',
};

const CheckClaimsActionButton = ({
    id,
    label,
    onClick,
    disabled = false,
    tone = 'view',
    children,
}) => (
    <OverlayTrigger
        placement="top"
        overlay={<Tooltip id={id}>{label}</Tooltip>}
    >
        <span className="table-action-icon-wrap">
            <button
                type="button"
                className={`table-action-icon-btn table-action-icon-btn--${tone}`}
                onClick={onClick}
                disabled={disabled}
                aria-label={label}
            >
                {children}
            </button>
        </span>
    </OverlayTrigger>
);

const AdminTripsCheckClaims = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { isAuthenticated } = useSelector((state) => state.adminAuth);
    const checkClaimsState = useSelector((state) => state.trips.checkClaims);
    const deleteState = useSelector((state) => state.trips.deleteCheckClaim);

    const {
        filters,
        setFilters,
        appliedFilters,
        setAppliedFilters,
        currentPage,
        setCurrentPage,
        itemsPerPage,
        companyPartnerSearch,
        setCompanyPartnerSearch,
        clearSearchState,
    } = usePersistedSearchState('trips-check-claims', EMPTY_CHECK_CLAIMS_FILTERS, {
        persistWhen: isAuthenticated,
        withCompanyPartnerSearch: true,
    });
    const [filterPartners, setFilterPartners] = useState([]);
    const [filterPartnersLoading, setFilterPartnersLoading] = useState(false);
    const [filterPartnersError, setFilterPartnersError] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [checkClaimToDelete, setCheckClaimToDelete] = useState(null);

    const partnerCompanyOptions = useMemo(() => {
        const names = filterPartners
            .map((partner) => partner.company_name?.trim())
            .filter((name) => name);
        return Array.from(new Set(names)).sort((a, b) => a.localeCompare(b, 'fr'));
    }, [filterPartners]);

    const handleDetails = (item) => {
        const checkClaimId = item.check_claim_id || item.forecast_id;
        if (checkClaimId) {
            navigate(`/admin/trips/check-claims/${checkClaimId}`, {
                state: { checkClaim: item }
            });
        }
    };

    const handleWeatherDetails = (item) => {
        const reservationNumber = item?.activity_info?.reservation_number;
        const policyCode = item?.policy_info?.policy_code;
        if (!reservationNumber || !policyCode) {
            return;
        }
        const params = new URLSearchParams({
            reservation_number: String(reservationNumber).trim(),
            policy_code: String(policyCode).trim(),
        });
        navigate(`/admin/trips/check-claims/weather-details?${params.toString()}`);
    };

    // Convertir une date YYYY-MM-DD en DD/MM/YYYY
    const convertDateToDDMMYYYY = (dateStr) => {
        if (!dateStr) return '';
        if (dateStr.includes('/')) return dateStr; // Déjà au format DD/MM/YYYY
        if (dateStr.includes('-')) {
            const [year, month, day] = dateStr.split('-');
            return `${day}/${month}/${year}`;
        }
        return dateStr;
    };

    // Charger les check claims avec pagination et filtres
    const loadCheckClaims = useCallback((page = 1, filtersToUse = null) => {
        const skip = (page - 1) * itemsPerPage;
        const params = {
            skip,
            limit: itemsPerPage,
        };

        // Utiliser les filtres fournis ou les filtres appliqués
        const activeFilters = filtersToUse !== null ? filtersToUse : appliedFilters;

        // Ajouter les filtres non vides
        Object.keys(activeFilters).forEach((key) => {
            if (activeFilters[key] && activeFilters[key].trim() !== '') {
                // Convertir les dates activity_end_date_from et activity_end_date_to en DD/MM/YYYY
                if (key === 'activity_end_date_from' || key === 'activity_end_date_to') {
                    params[key] = convertDateToDDMMYYYY(activeFilters[key].trim());
                } else {
                    params[key] = activeFilters[key].trim();
                }
            }
        });

        dispatch(fetchTripsCheckClaimsRequest(params));
    }, [itemsPerPage, appliedFilters, dispatch]);

    useEffect(() => {
        if (isAuthenticated) {
            loadCheckClaims(currentPage);
        }
        /* eslint-disable-next-line */
    }, [isAuthenticated]);

    useEffect(() => {
        const loadFilterPartners = async () => {
            try {
                setFilterPartnersLoading(true);
                setFilterPartnersError(null);
                const response = await getAllPartners();
                const partnersArray = response?.users && Array.isArray(response.users)
                    ? response.users
                    : (Array.isArray(response) ? response : []);
                setFilterPartners(partnersArray);
            } catch (error) {
                setFilterPartners([]);
                setFilterPartnersError(handleApiError(error, 'Erreur lors du chargement des partenaires'));
            } finally {
                setFilterPartnersLoading(false);
            }
        };
        if (isAuthenticated) {
            loadFilterPartners();
        }
    }, [isAuthenticated]);

    useEffect(() => {
        loadCheckClaims(currentPage);
    }, [currentPage, loadCheckClaims]);

    // Recharger la liste après suppression réussie
    useEffect(() => {
        if (deleteState.success) {
            setShowDeleteModal(false);
            setCheckClaimToDelete(null);
            // Masquer le message de succès après 3 secondes et recharger
            const timer = setTimeout(() => {
                loadCheckClaims(currentPage);
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [deleteState.success, currentPage, loadCheckClaims]);

    // Extraire les données et métadonnées de la réponse API
    const checkClaimsData = checkClaimsState?.data?.data || checkClaimsState?.data || [];
    const checkClaimsArray = Array.isArray(checkClaimsData) ? checkClaimsData : [];
    const meta = checkClaimsState?.data?.meta || {
        total: 0,
        skip: 0,
        limit: itemsPerPage,
        page: currentPage,
        total_pages: 0,
    };

    // Pagination côté serveur - utiliser les métadonnées de l'API
    const totalPages = meta.total_pages || Math.max(1, Math.ceil(meta.total / itemsPerPage));
    const totalItems = meta.total || 0;
    const indexOfFirstItem = meta.skip || 0;
    const indexOfLastItem = Math.min(indexOfFirstItem + checkClaimsArray.length, totalItems);

    const handleRefresh = () => {
        loadCheckClaims(currentPage);
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleApplyFilters = () => {
        const companyName = (filters.company_name || companyPartnerSearch).trim();
        const nextFilters = { ...filters, company_name: companyName };
        setFilters(nextFilters);
        setCompanyPartnerSearch(companyName);
        setAppliedFilters(nextFilters);
        setCurrentPage(1);
        loadCheckClaims(1, nextFilters);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleApplyFilters();
        }
    };

    const handleClearFilters = () => {
        const emptyFilters = { ...EMPTY_CHECK_CLAIMS_FILTERS };
        clearSearchState();
        setTimeout(() => {
            loadCheckClaims(1, emptyFilters);
        }, 100);
    };

    // Calculate pagination range (show max 5 pages around current)
    const getPaginationRange = () => {
        const maxVisiblePages = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        if (endPage - startPage < maxVisiblePages - 1) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        return { startPage, endPage };
    };

    // Utiliser directement les check claims de l'API (pas de filtrage côté client)
    const currentCheckClaims = checkClaimsArray;

    const getDeep = (obj, path, fallback = 'N/A') => {
        try {
            const val = path.reduce((acc, k) => (acc && acc[k] !== undefined ? acc[k] : undefined), obj);
            return val ?? fallback;
        } catch {
            return fallback;
        }
    };

    const handleDeleteClick = (item) => {
        const checkClaimId = item.check_claim_id || item.forecast_id;
        if (checkClaimId) {
            setCheckClaimToDelete({ id: checkClaimId, item });
            setShowDeleteModal(true);
        }
    };

    const handleCloseDeleteModal = () => {
        setShowDeleteModal(false);
        setCheckClaimToDelete(null);
    };

    const handleConfirmDelete = () => {
        if (checkClaimToDelete && checkClaimToDelete.id) {
            dispatch(deleteTripCheckClaimRequest(checkClaimToDelete.id));
        }
    };

    return (
        <div className="dashboard-page admin-trips-claims-page admin-trips-check-claims-page">
            <Card>
                <Card.Body>
                    <div className="page-header">
                        <div>
                            <h2 className="page-title">
                                <FiCheckCircle className="me-2" />
                                Trip - Check Claims
                            </h2>
                            <p className="text-muted mb-0">Vérification des sinistres de séjours</p>
                        </div>
                        <div className="d-flex gap-2 flex-wrap">
                            <Button
                                variant="success"
                                onClick={() => navigate('/admin/trips/check-claims/export', { state: { filters: { ...appliedFilters } } })}
                                className="d-flex align-items-center"
                            >
                                <FiDownload className="me-2" /> Export Excel
                            </Button>
                            <Button
                                variant="info"
                                onClick={() => navigate('/admin/trips/check-claims/weather-details', {
                                    state: {
                                        filters: {
                                            policy_code: appliedFilters.policy_code || '',
                                            reservation_number: appliedFilters.reservation_number || '',
                                            client_email: appliedFilters.client_email || '',
                                        },
                                    },
                                })}
                                className="d-flex align-items-center text-white"
                            >
                                <FiCloud className="me-2" /> Weather Details
                            </Button>
                            <Button
                                variant="outline-secondary"
                                onClick={() => navigate(-1)}
                                className="d-flex align-items-center"
                            >
                                <FiArrowLeft className="me-2" /> Retour
                            </Button>
                        </div>
                    </div>

                    {checkClaimsState.error && (
                        <Alert variant="danger" className="mt-3">
                            {checkClaimsState.error}
                        </Alert>
                    )}

                    {deleteState.success && (
                        <Alert variant="success" className="mt-3" dismissible onClose={() => { }}>
                            Check claim supprimé avec succès.
                        </Alert>
                    )}

                    {deleteState.error && (
                        <Alert variant="danger" className="mt-3" dismissible onClose={() => { }}>
                            {deleteState.error}
                        </Alert>
                    )}

                    {/* Filtres avancés */}
                    <Card className="mb-3">
                        <Card.Header className="d-flex justify-content-between align-items-center">
                            <h6 className="mb-0">Filtres de recherche</h6>
                            <div className="d-flex gap-2">
                                <Button variant="outline-secondary" size="sm" onClick={handleClearFilters}>
                                    Réinitialiser
                                </Button>
                                <Button variant="primary" size="sm" onClick={handleApplyFilters}>
                                    Appliquer les filtres
                                </Button>
                            </div>
                        </Card.Header>
                        <Card.Body>
                            <Row className="g-3">
                                <Col md={3}>
                                    <Form.Group className="mb-0">
                                        <Form.Label>Code police</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="policy_code"
                                            value={filters.policy_code}
                                            onChange={handleFilterChange}
                                            onKeyPress={handleKeyPress}
                                            placeholder="Ex: 301826051"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Label>Nom de société</Form.Label>
                                    <PartnerCompanyAutocomplete
                                        value={filters.company_name}
                                        onChange={(companyName) => setFilters((prev) => ({ ...prev, company_name: companyName }))}
                                        onInputChange={setCompanyPartnerSearch}
                                        options={partnerCompanyOptions}
                                        loading={filterPartnersLoading}
                                        error={filterPartnersError}
                                        onKeyDown={handleKeyPress}
                                        placeholder="Rechercher un partenaire…"
                                    />
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-0">
                                        <Form.Label>Numéro de réservation</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="reservation_number"
                                            value={filters.reservation_number}
                                            onChange={handleFilterChange}
                                            onKeyPress={handleKeyPress}
                                            placeholder="Numéro de réservation"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-0">
                                        <Form.Label>Email client</Form.Label>
                                        <Form.Control
                                            type="email"
                                            name="client_email"
                                            value={filters.client_email}
                                            onChange={handleFilterChange}
                                            onKeyPress={handleKeyPress}
                                            placeholder="Email du client"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-0">
                                        <Form.Label>Date fin activité (de)</Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="activity_end_date_from"
                                            value={filters.activity_end_date_from}
                                            onChange={handleFilterChange}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-0">
                                        <Form.Label>Date fin activité (à)</Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="activity_end_date_to"
                                            value={filters.activity_end_date_to}
                                            onChange={handleFilterChange}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-0">
                                        <Form.Label>Code sinistre</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="claim_code"
                                            value={filters.claim_code}
                                            onChange={handleFilterChange}
                                            onKeyPress={handleKeyPress}
                                            placeholder="Code du sinistre"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-0">
                                        <Form.Label>Date du sinistre</Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="date_of_claim"
                                            value={filters.date_of_claim}
                                            onChange={handleFilterChange}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-0">
                                        <Form.Label>Jour du séjour</Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="trip_day"
                                            value={filters.trip_day}
                                            onChange={handleFilterChange}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-0">
                                        <Form.Label>Nom référence</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="reference_name"
                                            value={filters.reference_name}
                                            onChange={handleFilterChange}
                                            onKeyPress={handleKeyPress}
                                            placeholder="Nom de la référence"
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    <div className="d-flex justify-content-end gap-2 mb-3">
                        <Button
                            variant="outline-secondary"
                            onClick={handleRefresh}
                            disabled={checkClaimsState.loading}
                            className="d-flex align-items-center"
                        >
                            <FiRefreshCw className={`me-2 ${checkClaimsState.loading ? 'spinning' : ''}`} />
                            Actualiser
                        </Button>
                    </div>

                    {checkClaimsState.loading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3">Chargement des check claims...</p>
                        </div>
                    ) : checkClaimsState.error ? (
                        <div className="text-center py-5">
                            <Alert variant="danger">{checkClaimsState.error}</Alert>
                        </div>
                    ) : currentCheckClaims.length === 0 ? (
                        <div className="text-center py-5">
                            <FiCheckCircle size={48} className="text-muted mb-3" />
                            <p className="text-muted">Aucun check claim disponible</p>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <div className="table-scroll">
                                <Table hover striped className="admin-table">
                                    <thead>
                                        <tr>
                                            <th>Entreprise</th>
                                            <th>Code police</th>
                                            <th>Numéro réservation</th>
                                            <th>Prénom</th>
                                            <th>Nom</th>
                                            <th>Email client</th>
                                            <th>Forecast déclenché</th>
                                            <th>Jour du séjour</th>
                                            <th className="col-actions">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {Array.isArray(currentCheckClaims) && currentCheckClaims.length > 0 ? (
                                            currentCheckClaims.map((item, idx) => (
                                                <tr key={item.check_claim_id || item.forecast_id || idx}>
                                                    <td>{getDeep(item, ['partner_info', 'company_name'], 'N/A')}</td>
                                                    <td style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                                        {getDeep(item, ['policy_info', 'policy_code'], 'N/A')}
                                                    </td>
                                                    <td>{getDeep(item, ['activity_info', 'reservation_number'], 'N/A')}</td>
                                                    <td>{getDeep(item, ['client_info', 'first_name'], 'N/A')}</td>
                                                    <td>{getDeep(item, ['client_info', 'last_name'], 'N/A')}</td>
                                                    <td>{getDeep(item, ['client_info', 'email'], 'N/A')}</td>
                                                    <td>
                                                        <Badge bg={getDeep(item, ['is_triggered_forecast']) ? 'success' : 'secondary'}>
                                                            {getDeep(item, ['is_triggered_forecast']) ? 'Oui' : 'Non'}
                                                        </Badge>
                                                    </td>
                                                    <td>
                                                        {getDeep(item, ['forecast_info', 'trip_day'], getDeep(item, ['trip_day'], 'N/A'))}
                                                    </td>
                                                    <td className="col-actions">
                                                        <div className="table-action-group" role="group" aria-label="Actions sur le sinistre">
                                                            <CheckClaimsActionButton
                                                                id={`view-${item.check_claim_id || item.forecast_id || idx}`}
                                                                label="Voir les détails du sinistre"
                                                                tone="view"
                                                                onClick={() => handleDetails(item)}
                                                            >
                                                                <FiEye size={16} aria-hidden="true" />
                                                            </CheckClaimsActionButton>
                                                            <CheckClaimsActionButton
                                                                id={`weather-${item.check_claim_id || item.forecast_id || idx}`}
                                                                label="Voir les détails météo"
                                                                tone="weather"
                                                                onClick={() => handleWeatherDetails(item)}
                                                                disabled={
                                                                    !item?.activity_info?.reservation_number
                                                                    || !item?.policy_info?.policy_code
                                                                }
                                                            >
                                                                <FiCloud size={16} aria-hidden="true" />
                                                            </CheckClaimsActionButton>
                                                            <CheckClaimsActionButton
                                                                id={`delete-${item.check_claim_id || item.forecast_id || idx}`}
                                                                label="Supprimer le sinistre"
                                                                tone="delete"
                                                                onClick={() => handleDeleteClick(item)}
                                                            >
                                                                <FiTrash2 size={16} aria-hidden="true" />
                                                            </CheckClaimsActionButton>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="9" className="text-center">
                                                    Aucun check claim trouvé
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </div>
                    )}

                    {/* Pagination */}
                    {totalItems > 0 && totalPages > 1 && (
                        <div className="d-flex justify-content-between align-items-center mt-4">
                            <div className="text-muted">
                                Affichage de {indexOfFirstItem + 1} à {indexOfLastItem} sur {totalItems} check claim{totalItems > 1 ? 's' : ''}
                            </div>
                            <Pagination className="mb-0">
                                <Pagination.First
                                    onClick={() => {
                                        setCurrentPage(1);
                                        loadCheckClaims(1);
                                    }}
                                    disabled={currentPage === 1}
                                />
                                <Pagination.Prev
                                    onClick={() => {
                                        const prevPage = currentPage - 1;
                                        setCurrentPage(prevPage);
                                        loadCheckClaims(prevPage);
                                    }}
                                    disabled={currentPage === 1}
                                />
                                {(() => {
                                    const { startPage, endPage } = getPaginationRange();
                                    const pages = [];

                                    // Show first page if not in range
                                    if (startPage > 1) {
                                        pages.push(
                                            <Pagination.Item key={1} onClick={() => {
                                                setCurrentPage(1);
                                                loadCheckClaims(1);
                                            }}>
                                                1
                                            </Pagination.Item>
                                        );
                                        if (startPage > 2) {
                                            pages.push(<Pagination.Ellipsis key="ellipsis-start" />);
                                        }
                                    }

                                    // Show pages in range
                                    for (let i = startPage; i <= endPage; i++) {
                                        pages.push(
                                            <Pagination.Item
                                                key={i}
                                                active={i === currentPage}
                                                onClick={() => {
                                                    setCurrentPage(i);
                                                    loadCheckClaims(i);
                                                }}
                                            >
                                                {i}
                                            </Pagination.Item>
                                        );
                                    }

                                    // Show last page if not in range
                                    if (endPage < totalPages) {
                                        if (endPage < totalPages - 1) {
                                            pages.push(<Pagination.Ellipsis key="ellipsis-end" />);
                                        }
                                        pages.push(
                                            <Pagination.Item key={totalPages} onClick={() => {
                                                setCurrentPage(totalPages);
                                                loadCheckClaims(totalPages);
                                            }}>
                                                {totalPages}
                                            </Pagination.Item>
                                        );
                                    }

                                    return pages;
                                })()}
                                <Pagination.Next
                                    onClick={() => {
                                        const nextPage = currentPage + 1;
                                        setCurrentPage(nextPage);
                                        loadCheckClaims(nextPage);
                                    }}
                                    disabled={currentPage === totalPages}
                                />
                                <Pagination.Last
                                    onClick={() => {
                                        setCurrentPage(totalPages);
                                        loadCheckClaims(totalPages);
                                    }}
                                    disabled={currentPage === totalPages}
                                />
                            </Pagination>
                        </div>
                    )}

                    {/* Modal de confirmation de suppression */}
                    <Modal show={showDeleteModal} onHide={handleCloseDeleteModal} centered>
                        <Modal.Header closeButton>
                            <Modal.Title>Confirmer la suppression</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <p>Êtes-vous sûr de vouloir supprimer ce check claim ?</p>
                            {checkClaimToDelete && (
                                <div className="mt-3">
                                    <p className="mb-1"><strong>ID:</strong> {checkClaimToDelete.id}</p>
                                    {checkClaimToDelete.item && (
                                        <>
                                            <p className="mb-1">
                                                <strong>Code police:</strong> {getDeep(checkClaimToDelete.item, ['policy_info', 'policy_code'], 'N/A')}
                                            </p>
                                            <p className="mb-1">
                                                <strong>Numéro réservation:</strong> {getDeep(checkClaimToDelete.item, ['activity_info', 'reservation_number'], 'N/A')}
                                            </p>
                                        </>
                                    )}
                                </div>
                            )}
                            <p className="text-danger mt-3 mb-0"><small>Cette action est irréversible.</small></p>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={handleCloseDeleteModal} disabled={deleteState.loading}>
                                Annuler
                            </Button>
                            <Button variant="danger" onClick={handleConfirmDelete} disabled={deleteState.loading}>
                                {deleteState.loading ? (
                                    <>
                                        <Spinner animation="border" size="sm" className="me-2" />
                                        Suppression...
                                    </>
                                ) : (
                                    'Supprimer'
                                )}
                            </Button>
                        </Modal.Footer>
                    </Modal>
                </Card.Body>
            </Card>
        </div>
    );
};

export default AdminTripsCheckClaims;

